import contour from 'maplibre-contour';
import { MercatorCoordinate } from 'maplibre-gl';
import { TERRAIN_PROVIDER } from '../domain/map-style';

/** Shared DEM cache for contours and explicit camera-height sampling. */
let source: InstanceType<typeof contour.DemSource> | undefined;
export function getTerrainDemSource() {
  return source ??= new contour.DemSource({
    cacheSize: 100, encoding: TERRAIN_PROVIDER.encoding, id: 'ride-terrain', maxzoom: 12,
    timeoutMs: 10_000, url: TERRAIN_PROVIDER.tileUrlTemplate, worker: true,
  });
}
type DemTile = Awaited<ReturnType<ReturnType<typeof getTerrainDemSource>['getDemTile']>>;
interface Tile { dem: DemTile; z: number; x: number; y: number }

/** Missing high-resolution tiles use the provider's available parent DEM. */
export function createTerrainElevationSampler(load: (z: number, x: number, y: number) => Promise<DemTile>, maxZoom = 12) {
  const tiles = new Map<string, Promise<Tile>>();
  const tileAt = (z: number, x: number, y: number): Promise<Tile> => {
    const count = 2 ** z;
    x = ((x % count) + count) % count; y = Math.max(0, Math.min(count - 1, y));
    const key = `${z}:${x}:${y}`;
    let tile = tiles.get(key);
    if (!tile) {
      tile = load(z, x, y).then(dem => ({ dem, z, x, y })).catch(error => {
        if (z > 0 && /404/.test(String(error))) return tileAt(z - 1, Math.floor(x / 2), Math.floor(y / 2));
        tiles.delete(key); throw error;
      });
      tiles.set(key, tile);
      if (tiles.size > 64) tiles.delete(tiles.keys().next().value!);
    }
    return tile;
  };
  const valueAt = (tile: Tile, worldX: number, worldY: number) => {
    const { dem, z, x, y } = tile;
    const px = Math.max(0, Math.min(dem.width - 1, (worldX * 2 ** z - x) * dem.width - .5));
    const py = Math.max(0, Math.min(dem.height - 1, (worldY * 2 ** z - y) * dem.height - .5));
    const i = Math.floor(px), j = Math.floor(py), fx = px - i, fy = py - j;
    const sample = (cx: number, cy: number) => {
      const value = dem.data[Math.min(dem.height - 1, cy) * dem.width + Math.min(dem.width - 1, cx)];
      if (value === undefined || !Number.isFinite(value) || value < -12000 || value > 9000) throw new Error('Terrain elevation is unavailable at this camera position.');
      return value;
    };
    return (sample(i, j) * (1 - fx) + sample(i + 1, j) * fx) * (1 - fy)
      + (sample(i, j + 1) * (1 - fx) + sample(i + 1, j + 1) * fx) * fy;
  };
  return async (coordinate: readonly [number, number], signal?: AbortSignal): Promise<number> => {
    signal?.throwIfAborted();
    const point = MercatorCoordinate.fromLngLat([((coordinate[0] + 540) % 360) - 180,
      Math.max(-85.051128, Math.min(85.051128, coordinate[1]))]);
    const tile = await tileAt(maxZoom, Math.floor(point.x * 2 ** maxZoom), Math.floor(point.y * 2 ** maxZoom));
    const count = 2 ** tile.z, width = tile.dem.width, height = tile.dem.height;
    const px = point.x * count * width - .5, py = point.y * count * height - .5;
    const x = Math.floor(px), y = Math.floor(py), fx = px - x, fy = py - y;
    const sample = async (gx: number, gy: number) => {
      const tx = Math.floor(gx / width), ty = Math.floor(gy / height);
      const found = await tileAt(tile.z, tx, ty);
      const wx = (((gx + .5) / (count * width)) % 1 + 1) % 1;
      return valueAt(found, wx, Math.max(0, Math.min(1 - 1e-12, (gy + .5) / (count * height))));
    };
    const [a, b, c, d] = await Promise.all([sample(x, y), sample(x + 1, y), sample(x, y + 1), sample(x + 1, y + 1)]);
    signal?.throwIfAborted();
    return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
  };
}

export const terrainElevationAt = createTerrainElevationSampler((z, x, y) => getTerrainDemSource().getDemTile(z, x, y));
